<script setup lang="ts">
const { imageUrl } = defineProps<{ imageUrl: string }>();

const emit = defineEmits<{
  (e: "click"): void;
}>();
const isZoomedIn = defineModel<boolean>("isZoomedIn", { default: false });

const TOTAL_IMAGES = 36;
const PHYSICS_SENSITIVITY = 10;
const MIN_ZOOM_SCALE = 1;
const MAX_ZOOM_SCALE = 4;
const ZOOM_RESET_DURATION_MS = 100;

const DEFAULT_START_IMAGE_INDEX = 0;
const TOUCH_MOVE_THROTTLE_MS = 8;
const GESTURE_PIXEL_THRESHOLD_PX = 5;
const GESTURE_TIME_THRESHOLD_MS = 50;

const { isTouchDevice } = useDeviceInformation();

const {
  currentImageIndex,
  isDragging,
  hasDragged,
  isAnimating,
  predictedSettleIndex,
  settledIndex,
  stableIndex,
  startDrag,
  onDrag,
  endDrag,
  stopAnimation,
} = use360Physics(
  TOTAL_IMAGES,
  DEFAULT_START_IMAGE_INDEX,
  PHYSICS_SENSITIVITY,
  isTouchDevice,
);

const {
  isInitialLoading,
  isCurrentImageLoading,
  isFullyLoaded,
  currentImageError,
  currentImageUrl,
  loadImageWithFeedback,
  ensureHighRes,
} = use360ImageLoader(() => imageUrl, currentImageIndex, TOTAL_IMAGES);

const currentScale = ref(MIN_ZOOM_SCALE);

const scrollZoomRef = useTemplateRef("scrollZoomRef");

const resetZoom = async () => {
  if (currentScale.value === MIN_ZOOM_SCALE) return;

  await scrollZoomRef.value?.animateReset(ZOOM_RESET_DURATION_MS);
};

const updateIsZoomedIn = () => {
  isZoomedIn.value = currentScale.value > MIN_ZOOM_SCALE;
};

watch(isZoomedIn, async (newVal, oldVal) => {
  if (oldVal && !newVal) {
    await resetZoom();
  }
});

watch([currentScale], () => {
  updateIsZoomedIn();
});

defineExpose({ resetZoom });

// Prefetch high-res frames based on physics predictions/states
watch(
  [predictedSettleIndex, stableIndex, settledIndex],
  ([predictedIdx, stableIdx, settledIdx]) => {
    if (predictedIdx != null) ensureHighRes(predictedIdx);

    if (stableIdx != null) ensureHighRes(stableIdx);

    if (settledIdx != null) ensureHighRes(settledIdx);
  },
  { immediate: true },
);

const lastTouchMoveTimeMs = ref(0);
const touchStartX = ref(0);
const touchStartY = ref(0);
const touchStartTime = ref(0);
const hasDeterminedGesture = ref(false);

const shouldShowSlider = computed(
  () => isFullyLoaded?.value && !isDragging.value && !isAnimating.value,
);

const showLoading = computed(
  () => isInitialLoading.value || isCurrentImageLoading?.value,
);

const handleImageClick = () => {
  if (!hasDragged.value) emit("click");
};

const handleMouseDown = (event: MouseEvent) => {
  if (currentScale.value > MIN_ZOOM_SCALE) return;
  event.preventDefault();
  startDrag(event.clientX);
  document.body.style.cursor = "grabbing";
};

const handleMouseMove = (event: MouseEvent) => {
  if (currentScale.value > MIN_ZOOM_SCALE || !isDragging.value) return;
  event.preventDefault();
  onDrag(event.clientX);
};

const handleMouseUp = () => {
  if (currentScale.value <= MIN_ZOOM_SCALE && isDragging.value) {
    endDrag();
    document!.body!.style!.cursor = "";
  }
};

const handleImageTouchStart = (event: TouchEvent) => {
  if (event.touches.length > 1) return;

  const touch = event.touches[0];
  if (!touch) return;
  touchStartX.value = touch.clientX;
  touchStartY.value = touch.clientY;
  touchStartTime.value = Date.now();
  hasDeterminedGesture.value = false;
};

const handleTouchMove = (event: TouchEvent) => {
  if (event.touches.length > 1) return;

  const touch = event.touches[0];
  const now = Date.now();
  if (!touch) return;

  if (now - lastTouchMoveTimeMs.value < TOUCH_MOVE_THROTTLE_MS) return;

  const diffX = Math.abs(touch.clientX - touchStartX.value);
  const diffY = Math.abs(touch.clientY - touchStartY.value);
  const timeDelta = now - touchStartTime.value;

  if (
    !hasDeterminedGesture.value &&
    (diffX > GESTURE_PIXEL_THRESHOLD_PX ||
      diffY > GESTURE_PIXEL_THRESHOLD_PX ||
      timeDelta > GESTURE_TIME_THRESHOLD_MS)
  ) {
    hasDeterminedGesture.value = true;
    // Decide gesture: horizontal rotation vs allow vertical scroll
    if (
      currentScale.value <= MIN_ZOOM_SCALE &&
      diffX > diffY &&
      diffX > GESTURE_PIXEL_THRESHOLD_PX
    ) {
      startDrag(touchStartX.value);
      event.preventDefault(); // lock into horizontal rotation
    } else {
      // vertical intent -> don't prevent default so page can scroll
    }
  }

  if (currentScale.value <= MIN_ZOOM_SCALE && isDragging.value) {
    // Continue horizontal rotation
    event.preventDefault();
    lastTouchMoveTimeMs.value = now;
    onDrag(touch.clientX);
  }
};

const handleTouchEnd = () => {
  hasDeterminedGesture.value = false;
  if (isDragging.value) endDrag();
};

const handleMouseLeave = () => {
  if (isDragging.value) endDrag();
  document!.body!.style!.cursor = "";
};

onUnmounted(() => {
  document!.body!.style!.cursor = "";
  stopAnimation();
});

const updateScale = (newScale?: number | string) => {
  if (!newScale) return;
  currentScale.value = Number(newScale) / SCALE_CONVERSION_FACTOR;
  updateIsZoomedIn();
};
</script>

<template>
  <div
    class="relative size-full overflow-hidden"
    :class="{
      'cursor-wait': showLoading,
      'select-none': isDragging,
    }"
    tabindex="0"
    role="img"
    aria-label="360° Product View. Use arrow keys or slider to rotate."
    :aria-busy="showLoading"
  >
    <div
      class="relative size-full flex items-center justify-center pb-0 touch-pan-y touch-pinch-zoom"
      data-test="360-viewer-image"
    >
      <div
        class="size-full flex items-center justify-center overflow-hidden relative touch-pan-y touch-pinch-zoom"
        :class="{
          'cursor-grab': !isDragging,
          'cursor-grabbing': isDragging,
        }"
        data-test="360-viewer-zoom-container"
        @click="handleImageClick"
        @keydown="handleImageClick"
        @mousemove="handleMouseMove"
        @mousedown="handleMouseDown"
        @mouseup="handleMouseUp"
        @mouseleave="handleMouseLeave"
        @blur="handleMouseLeave"
      >
        <div
          v-if="showLoading"
          class="absolute inset-0 bg-white/20 backdrop-blur-sm motion-reduce:backdrop-blur-0 flex-1 z-20 pointer-events-none transition-opacity duration-200 ease-in-out"
        >
          <BaseLoadingSpinner />
        </div>

        <div
          v-if="currentImageError"
          class="absolute inset-0 z-30 flex flex-col items-center justify-center gap-3 px-4 text-center text-sm text-white bg-black/40"
          role="alert"
        >
          <span>{{ $t("360Images.error") }}</span>
          <BaseButton @click="loadImageWithFeedback(currentImageIndex)">
            {{ $t("360Images.retry") }}
          </BaseButton>
        </div>

        <ScrollZoomContainer
          ref="scrollZoomRef"
          v-model="currentScale"
          :min-scale="MIN_ZOOM_SCALE"
          :max-scale="MAX_ZOOM_SCALE"
        >
          <NuxtImg
            :src="currentImageUrl"
            alt="360° Product View - 360° view"
            class="size-full object-contain select-none pointer-events-auto origin-center scale-100 transition-opacity duration-150 ease-in-out motion-reduce:transition-none"
            draggable="false"
            @touchmove="handleTouchMove"
            @touchend.passive="handleTouchEnd"
            @touchstart.passive="handleImageTouchStart"
          />
        </ScrollZoomContainer>
      </div>

      <LazyProductMedia360ViewerSlider
        v-if="!showLoading && !currentImageError"
        :model-value="currentImageIndex"
        :hide-slider="!shouldShowSlider"
        orient="horizontal"
        :min="0"
        :max="TOTAL_IMAGES - 1"
        class="absolute -bottom-2.5 lg:-bottom-[1px] left-1/2 -translate-x-1/2 z-10 transition-all duration-300 ease-in-out max-w-[60%]"
        @update:model-value="(value) => (currentImageIndex = Number(value))"
      >
        <template #firstLabel>0º</template>
        <template #secondLabel>360º</template>
      </LazyProductMedia360ViewerSlider>
      <LazyProductMedia360ViewerSlider
        v-if="!showLoading && !currentImageError"
        :model-value="currentScale * SCALE_CONVERSION_FACTOR"
        :hide-slider="!shouldShowSlider"
        orient="vertical"
        :min="MIN_ZOOM_SCALE * SCALE_CONVERSION_FACTOR"
        :max="MAX_ZOOM_SCALE * SCALE_CONVERSION_FACTOR"
        class="absolute top-1/2 -right-2.5 lg:-right-[1px] -translate-y-1/2 w-[54px] h-[60%] max-h-[320px] z-10 transition-all duration-300 ease-in-out"
        @update:model-value="updateScale"
      >
        <template #firstLabel>
          <BaseIcon
            name="zoomIn"
            :size="20"
          />
        </template>
        <template #secondLabel>
          <BaseIcon
            name="zoomOut"
            :size="20"
          />
        </template>
      </LazyProductMedia360ViewerSlider>
    </div>
  </div>
</template>
